import { z } from 'zod';
import { DEFAULT_SCHEDULE_VIEW, isScheduleView, SCHEDULE_VIEWS, type ScheduleView } from './scheduleEvents';

/**
 * Where the schedule's List/Day/Week choice is read from.
 *
 * Two untrusted sources answer the same question, and both may decline: the URL, so a view is linkable and the back
 * button restores it, and localStorage, so it is remembered across sessions. Neither is typed at the boundary, so both
 * are validated here and both are allowed to say "no view" — which is what lets one fall through to the other.
 */

/** The remembered choice. Restored on a fresh page load; the URL param outranks it. */
export const SCHEDULE_VIEW_STORAGE_KEY = 'liza:scheduleView';

/**
 * The `view` search param.
 *
 * Optional rather than defaulted: a default would make the param always present, leaving no absent state for the
 * remembered view to fill — and the nav's plain `/appointments` link would then re-stamp it on every visit, which is
 * the bug this whole module exists for (issue #8682). `catch` reads a view the schedule no longer offers as unasked-for
 * rather than as an error, so a stale link falls through to the remembered view instead of failing validation.
 */
export const ScheduleViewSearchParam = z.enum(SCHEDULE_VIEWS).optional().catch(undefined);

/**
 * Reads a stored view, or `undefined` for anything that is not one.
 *
 * Validating rather than trusting the read is load-bearing: a view name that leaves `SCHEDULE_VIEWS` outlives this
 * release in every browser that stored it, and the value indexes `FULL_CALENDAR_VIEWS` and picks the list branch — so
 * an unrecognized one would render an empty grid rather than falling back.
 */
export const parseStoredScheduleView = (raw: string | null): ScheduleView | undefined => {
  if (!raw) return undefined;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isScheduleView(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

/** The URL wins (bookmarks and shared links), then the remembered choice, then the day grid. */
export const resolveScheduleView = (
  urlView: ScheduleView | undefined,
  storedView: ScheduleView | undefined,
): ScheduleView => urlView ?? storedView ?? DEFAULT_SCHEDULE_VIEW;
